/**
 * BeraStays — Version & update check
 *
 * APP_VERSION is injected at build time by vite.config.ts (define).
 * A matching public/version.json is generated by the same plugin so the
 * running client can poll for newer builds and prompt the user to reload.
 */

// Injected by Vite `define`
declare const __APP_VERSION__: string;

export const APP_VERSION: string =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";

const VERSION_KEY = "berastays.app.version";
const PROMPTED_KEY = "berastays.app.promptedVersion";

export function getPromptedVersion(): string | null {
  try { return localStorage.getItem(PROMPTED_KEY); } catch { return null; }
}
export function setPromptedVersion(v: string): void {
  try { localStorage.setItem(PROMPTED_KEY, v); } catch { /* ignore */ }
}

/** Returns the previously stored version (or null on first run). */
export function getStoredVersion(): string | null {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch {
    return null;
  }
}

/** Persists the current build version locally. */
export function persistCurrentVersion(): void {
  try {
    localStorage.setItem(VERSION_KEY, APP_VERSION);
  } catch {
    /* ignore */
  }
}

export type RemoteVersionInfo = {
  version: string;
  minSupportedVersion?: string;
  storeUrls?: { android?: string; ios?: string };
};

/**
 * Check the remote /version.json. Returns full info (version, minSupportedVersion, storeUrls)
 * when a newer build is available, otherwise null.
 */
export async function fetchRemoteVersion(): Promise<RemoteVersionInfo | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as RemoteVersionInfo;
    if (!data.version) return null;
    if (data.version !== APP_VERSION) return data;
    return null;
  } catch {
    return null;
  }
}

/**
 * Start a background loop that checks for new builds while the tab is
 * visible. Fires `onNewVersion(remoteVersion)` once when a mismatch is
 * detected, then stops polling.
 */
export function startVersionWatcher(
  onNewVersion: (info: RemoteVersionInfo) => void,
  intervalMs = 30_000,
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tick = async () => {
    if (stopped) return;
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      const remote = await fetchRemoteVersion();
      if (remote && remote.version !== getPromptedVersion()) {
        stopped = true;
        setPromptedVersion(remote.version);
        onNewVersion(remote);
        return;
      }
    }
    timer = setTimeout(tick, intervalMs);
  };

  // First check after 8s to avoid racing initial load
  timer = setTimeout(tick, 8_000);

  // Force a check whenever the tab becomes visible / app resumes
  const onVisible = () => {
    if (!stopped && typeof document !== "undefined" && document.visibilityState === "visible") {
      tick();
    }
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
  }

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    }
  };
}

/**
 * Force an immediate version check. If a newer build is live, wipes service
 * worker caches and hard-reloads. Call this after sign-in.
 */
export async function forceVersionCheckAndReload(): Promise<boolean> {
  const remote = await fetchRemoteVersion();
  if (!remote) return false;
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update().catch(() => {})));
    }
  } catch { /* ignore */ }
  setPromptedVersion(remote.version);
  // Hard reload to fetch the new shell
  window.location.reload();
  return true;
}
